/**
 * 航迹：遥测快照 → 可画的位置、航迹点序列、更新节流。
 *
 * ## 这一层存在的理由：`null` 与 `0` 的区别
 *
 * `GLOBAL_POSITION_INT` 在 EKF 还没定位时会给出 `(0, 0)` —— 那是几内亚湾的
 * 一个点，不是"当前位置"。仓库里反复强调过这一类 bug，所以位置提取**不返回
 * 裸坐标**，而是返回带状态的结果：调用方拿到的永远是"能不能画"，而不是
 * "一个看起来能画但可能是假的坐标"。任何 `null` 都不允许被替换成 `0`。
 *
 * 反过来，(0,0) 本身是合法的地球坐标，所以不能一律拒绝 —— 判据是
 * **有没有定位**：`fixType` 明确低于二维定位时，(0,0) 一定是占位值；
 * 定位正常时它就是真实位置，照画（用户可能在赤道/本初子午线附近作业）。
 */

import { haversineDistanceM, polylineLengthM, type LatLon } from '@hgc/ardupilot';
import type { VehicleState } from '@hgc/core';

/** 航迹点数量上限。长时间飞行必须封顶，否则每秒几个点撑几个小时就是几十万个顶点，浏览器会被拖垮。 */
export const TRAIL_MAX_POINTS = 4000;

/**
 * 相邻航迹点的最小间距。
 *
 * 机体悬停时 GPS 噪声会让坐标在几米内反复抖动；照着画会让航迹变成一团毛球，
 * 而且顶点数暴涨。2 m 是"抖动能滤掉、真实位移不丢"的折中。
 */
export const TRAIL_MIN_DISTANCE_M = 2;

/** 航迹总长上限：超出后从**最早**的点开始丢（用户关心的是"刚飞过哪里"）。 */
export const TRAIL_MAX_LENGTH_M = 60_000;

/** 位置每秒最多推给地图几次。遥测可能 10–50 Hz，而屏幕上的位移在 4 Hz 下已经连续。 */
export const POSITION_MIN_INTERVAL_MS = 250;

/** 判定"定位有效"的最低 `fixType`（`MAV_GPS_FIX_TYPE`：2 = 2D 定位，3 = 3D 定位）。 */
export const MIN_TRUSTED_FIX_TYPE = 2;

export type PositionStatus =
  /** 坐标可用 */
  | 'ok'
  /** 还没收到位置消息 */
  | 'no-data'
  /** 收到了，但不是有效数字或越界 */
  | 'invalid'
  /** 收到了 (0,0) 且明确没有定位 —— 这是占位值，不是位置 */
  | 'no-fix';

export interface VehiclePosition {
  readonly point: LatLon | null;
  readonly status: PositionStatus;
  /** 面向用户的一句话说明；`status === 'ok'` 时为空串 */
  readonly note: string;
}

const NO_POSITION: VehiclePosition = Object.freeze({ point: null, status: 'no-data', note: '尚未收到位置数据' });

function pairOf(latDeg: number | null, lonDeg: number | null): LatLon | null {
  if (latDeg === null || lonDeg === null) return null;
  if (!Number.isFinite(latDeg) || !Number.isFinite(lonDeg)) return null;
  if (Math.abs(latDeg) > 90 || Math.abs(lonDeg) > 180) return null;
  return { latDeg, lonDeg };
}

/**
 * 焦点机体的当前位置。
 *
 * 高度（`altM`）刻意**不**带上：地图图层是二维的，把高度混进来会让"高度缺失"
 * 变成"位置缺失"，而这两件事在故障排查时指向完全不同的原因。
 */
export function vehiclePositionOf(state: VehicleState | null): VehiclePosition {
  if (state === null) return NO_POSITION;

  const point = pairOf(state.latDeg, state.lonDeg);
  if (point === null) {
    const hasAny = state.latDeg !== null || state.lonDeg !== null;
    return hasAny
      ? { point: null, status: 'invalid', note: '位置坐标无效或超出范围，已丢弃' }
      : { point: null, status: 'no-data', note: '尚未收到位置数据' };
  }

  if (point.latDeg === 0 && point.lonDeg === 0) {
    const fixType = state.fixType;
    if (fixType === null || fixType < MIN_TRUSTED_FIX_TYPE) {
      return {
        point: null,
        status: 'no-fix',
        note: 'GPS 尚未定位，坐标 (0,0) 是占位值，不作为位置显示',
      };
    }
  }

  return { point, status: 'ok', note: '' };
}

/** 返航点（`HOME_POSITION`）。判据与位置一致，避免同一类"占位坐标"从另一条路漏出去。 */
export function homePositionOf(state: VehicleState | null): LatLon | null {
  if (state === null) return null;
  const point = pairOf(state.homeLatDeg, state.homeLonDeg);
  if (point === null) return null;
  if (point.latDeg === 0 && point.lonDeg === 0 && (state.fixType ?? 0) < MIN_TRUSTED_FIX_TYPE) {
    return null;
  }
  return point;
}

export interface TrailOptions {
  readonly maxPoints: number;
  readonly minDistanceM: number;
  readonly maxLengthM: number;
}

export const DEFAULT_TRAIL_OPTIONS: TrailOptions = Object.freeze({
  maxPoints: TRAIL_MAX_POINTS,
  minDistanceM: TRAIL_MIN_DISTANCE_M,
  maxLengthM: TRAIL_MAX_LENGTH_M,
});

/**
 * 追加一个航迹点（纯函数版本，便于测试与"试算"）。
 *
 * 三种情况会让结果不变或变短：
 * 1. 与上一个点距离小于 `minDistanceM` → 不追加（悬停抖动）；
 * 2. 点数超过 `maxPoints` → 从头部丢；
 * 3. 折线总长超过 `maxLengthM` → 从头部丢到总长回到上限内。
 */
export function appendTrailPoint(
  trail: readonly LatLon[],
  next: LatLon,
  options: TrailOptions = DEFAULT_TRAIL_OPTIONS,
): readonly LatLon[] {
  const last = trail[trail.length - 1];
  if (last === undefined) return [next];
  if (haversineDistanceM(last, next) < options.minDistanceM) return trail;
  return trimTrail([...trail, next], options);
}

/** 按点数与总长上限裁剪。 */
export function trimTrail(
  trail: readonly LatLon[],
  options: TrailOptions = DEFAULT_TRAIL_OPTIONS,
): readonly LatLon[] {
  let result = trail;
  if (result.length > options.maxPoints) {
    result = result.slice(result.length - options.maxPoints);
  }
  // 总长裁剪是 O(n)，但只在超出上限后才会真的执行；上面那次切片已经把 n 封在 maxPoints 内
  while (result.length > 2 && polylineLengthM(result) > options.maxLengthM) {
    result = result.slice(1);
  }
  return result;
}

/** 航迹总长（米）。 */
export function trailLengthM(trail: readonly LatLon[]): number {
  return polylineLengthM(trail);
}

/**
 * 航迹缓冲。
 *
 * 和上面的纯函数相比多了一层"就地追加"：`appendTrailPoint` 每次都会复制数组，
 * 4000 个点、每秒几次的更新下虽然还扛得住，但没必要。这里内部持有可变数组，
 * 只在**真的裁掉头部**时做一次 `splice`，`points` 直接给出（读侧只读）。
 */
export class TrailBuffer {
  private points: LatLon[] = [];

  constructor(private readonly options: TrailOptions = DEFAULT_TRAIL_OPTIONS) {}

  get length(): number {
    return this.points.length;
  }

  /** 只读视图。内部数组原地增长，调用方只读它，因此不需要每次复制。 */
  view(): readonly LatLon[] {
    return this.points;
  }

  /** 追加；返回"是否真的加了点"，调用方据此决定要不要重绘（不做无谓的图层更新）。 */
  push(position: LatLon): boolean {
    const last = this.points[this.points.length - 1];
    if (last !== undefined && haversineDistanceM(last, position) < this.options.minDistanceM) {
      return false;
    }
    this.points.push(position);

    const overflow = this.points.length - this.options.maxPoints;
    if (overflow > 0) this.points.splice(0, overflow);

    while (this.points.length > 2 && polylineLengthM(this.points) > this.options.maxLengthM) {
      this.points.shift();
    }
    return true;
  }

  reset(): void {
    this.points = [];
  }

  /** 导出为普通数组（测试与"全览航迹"取包围盒用）。 */
  toArray(): readonly LatLon[] {
    return this.points;
  }
}

/**
 * 更新闸门（节流器）。
 *
 * 需要它的原因不是"渲染太慢"，而是**别让上游的频率决定下游的频率** ——
 * 遥测帧率会随链路变化（50 Hz 串口 / 5 Hz 数传），如果每帧都推一次图层数据，
 * 界面在好链路上反而更卡。这里把速率钉在 `minIntervalMs`，同时保证：
 * 位置没变就不推（悬停时前端完全静止），第一个点立刻通过（否则刚上电要等半秒才出图标）。
 *
 * 被丢掉的中间点不会进航迹：航迹的间距判据（2 m）比闸门间隔（250 ms）更细，
 * 在 30 m/s 的速度下两者相当。要画更密的航迹，应该调小 `minIntervalMs`，
 * 而不是绕过闸门 —— 绕过就回到了"上游越快越卡"。
 */
export class UpdateGate {
  private lastAcceptedAt = Number.NEGATIVE_INFINITY;
  private lastKey: string | null = null;

  constructor(private readonly minIntervalMs: number = POSITION_MIN_INTERVAL_MS) {}

  /** `at` 可注入，测试里不用真的等时钟。 */
  accept(point: LatLon | null, at: number = Date.now()): boolean {
    if (point === null) {
      // 位置丢失也要通知一次（界面上要能显示"位置未知"），但不能反复通知
      if (this.lastKey === null) return false;
      this.lastKey = null;
      this.lastAcceptedAt = at;
      return true;
    }
    const key = `${point.latDeg.toFixed(7)},${point.lonDeg.toFixed(7)}`;
    if (key === this.lastKey) return false;
    if (at - this.lastAcceptedAt < this.minIntervalMs) return false;
    this.lastKey = key;
    this.lastAcceptedAt = at;
    return true;
  }
}

/** 机体是否在移动。用于决定要不要画航向线（静止时航向是噪声）。 */
export function isMoving(state: VehicleState | null, thresholdMps = 0.5): boolean {
  if (state === null) return false;
  const groundspeed = state.groundspeedMps;
  if (groundspeed !== null) return groundspeed >= thresholdMps;
  // VFR_HUD 还没来时退回到速度矢量；两者都没有就按"不动"处理（不画航向线比画错好）
  const vx = state.vxMps;
  const vy = state.vyMps;
  if (vx === null || vy === null) return false;
  return Math.hypot(vx, vy) >= thresholdMps;
}
